import AnalyticsReportsApi from 'api/analytics-reports';
import type { ReportPreviewPayload } from 'api/analytics-reports/types';
import { isUnauthorizedApiClientError } from 'api/client/isUnauthorizedApiClientError';
import { CognitoTokenManager } from 'app/modules/auth';

export async function fetchAnalyticsReportPublicPreview(
  reportId: number,
  tokenFromUrl: string | null | undefined,
  accountId: number | undefined,
): Promise<ReportPreviewPayload> {
  const resolvedAccountId =
    accountId ?? CognitoTokenManager.getSelectedAccountId() ?? undefined;

  if (tokenFromUrl) {
    try {
      return await AnalyticsReportsApi.getReportPreview(reportId, {
        jwtToken: tokenFromUrl,
        accountId: resolvedAccountId,
      });
    } catch (error) {
      // The URL token can be either a Cognito access token (interactive flow)
      // or a BE-minted server-signed preview JWT (screenshot/PDF render flow).
      // Try as a Cognito Bearer first; on 401 fall back to the `?token=` query
      // form so the BE's ServerSignedAuthMiddleware can validate the preview
      // JWT.
      //
      // We must detect 401 from BOTH error shapes the API client produces:
      //   - request error path (axiosError.response.status)
      //   - response interceptor path (response.status), used after the global
      //     401 handler attempts a Cognito refresh and fails
      // `isUnauthorizedApiClientError` covers both — the local check that used
      // to live here only looked at axiosError and silently dropped the
      // refresh-failure case, so the fallback retry never fired.
      if (!isUnauthorizedApiClientError(error)) {
        throw error;
      }

      return AnalyticsReportsApi.getReportPreview(reportId, {
        previewToken: tokenFromUrl,
        accountId: resolvedAccountId,
      });
    }
  }

  const accessToken = await CognitoTokenManager.getAccessToken();

  if (accessToken) {
    return AnalyticsReportsApi.getReportPreview(reportId, {
      jwtToken: accessToken,
      accountId: resolvedAccountId,
    });
  }

  throw new Error('Report preview auth is not available');
}
